import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Controller, useFormContext } from 'react-hook-form'
import Select from 'react-select'
import { RootState } from 'constants/interfaces'
import { extractSidsFromGroupsMemoized } from 'redux/actions/config'
import useLocaleText from 'components/useLocaleText'
import { PasteIdsDialog } from './PasteIdsDialog'
import { useToast } from '@chakra-ui/core'

const customStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    boxShadow: 'none',
    borderColor: state.isFocused ? 'black' : provided.borderColor,
    '&:hover': { borderColor: 'black' },
    zIndex: 1,
  }),
  menu: (p: any) => ({ ...p, zIndex: 9999 }),
  option: (p: any, state: any) => ({
    ...p,
    backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
    color: 'black',
    cursor: 'pointer',
  }),
  multiValueLabel: (p: any) => ({ ...p, fontSize: '16px', fontWeight: 500 }),
  multiValueRemove: (p: any) => ({
    ...p,
    cursor: 'pointer',
    ':hover': { backgroundColor: '#eee', color: 'black' },
  }),
}

type Option = { value: string; label: string }

interface SidsPickerProps {
  name: string // 'products_cluster_data.store_id'
}

export const SidsPicker: React.FC<SidsPickerProps> = ({ name }) => {
  const { groups } = useSelector((s: RootState) => s.config.config)
  const { control, watch, setValue } = useFormContext()
  const t_select_sids = useLocaleText('select_sids', true)
  const t_nothing_added = useLocaleText('nothing_added', true)
  const t_ids_are_invalid_or_already_selected = useLocaleText('ids_are_invalid_or_already_selected', true)
  const toast = useToast()

  const allSids: string[] = useMemo(() => Array.from(new Set(extractSidsFromGroupsMemoized(groups))), [groups])

  const rawSelected = watch(name) as Array<string | Option> | undefined

  useEffect(() => {
    if (!rawSelected || rawSelected.length === 0) return
    const isStringArray = typeof rawSelected[0] === 'string'
    if (isStringArray) {
      const normalized: Option[] = (rawSelected as string[]).map((sid) => ({
        value: sid,
        label: sid,
      }))
      setValue(name, normalized, true)
    }
  }, [rawSelected, name, setValue])

  const selected: Option[] = useMemo(() => {
    if (!rawSelected) return []
    return (rawSelected as any[]).map((x) => (typeof x === 'string' ? { value: x, label: x } : (x as Option)))
  }, [rawSelected])

  const options: Option[] = useMemo(() => {
    const selectedSet = new Set(selected.map((o) => o.value))
    return allSids.filter((sid) => !selectedSet.has(sid)).map((sid) => ({ value: sid, label: sid }))
  }, [allSids, selected])

  const handlePasteConfirm = (ids: string[]) => {
    if (!ids || ids.length === 0) return

    const validSet = new Set(allSids)
    const current = new Set(selected.map((o) => o.value))

    let addedCount = 0
    ids.forEach((id) => {
      if (validSet.has(id) && !current.has(id)) {
        current.add(id)
        addedCount += 1
      }
    })

    if (addedCount === 0) {
      toast({
        title: t_nothing_added,
        description: t_ids_are_invalid_or_already_selected,
        status: 'error',
        position: 'bottom',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    const merged: string[] = Array.from(current)
    const normalized: Option[] = merged.map((sid) => ({ value: sid, label: sid }))
    setValue(name, normalized, true)
  }

  return (
    <div>
      <Controller
        name={name}
        control={control}
        defaultValue={selected}
        as={Select}
        isMulti
        options={options}
        placeholder={t_select_sids}
        styles={customStyles}
        onChange={([val]) => val || []}
      />

      <div style={{ marginTop: 8 }}>
        <PasteIdsDialog onConfirm={handlePasteConfirm} />
      </div>
    </div>
  )
}
